/**
 * useAIExtraction.js
 *
 * Hook for AI-powered document extraction using Claude API.
 * Handles API key management, extraction calls, and error handling.
 */

import { useState, useCallback, useMemo } from 'react';
import { useLegacyStorage } from './useLegacyStorage';
import {
  extractChecklistItems,
  fileToBase64,
  getMediaType
} from '../services/ai/extractionService';
import { TIER_LIMITS } from '../config/operations/checklistImportConfig';

// Storage keys
const USAGE_KEY = 'ai_extraction_usage';
const API_KEY_KEY = 'claude_api_key';
const TIER_KEY = 'subscription_tier';

/**
 * Get current month key for usage tracking
 */
const getCurrentMonthKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

/**
 * Hook for AI extraction operations
 */
export const useAIExtraction = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  const storage = useLegacyStorage();

  /**
   * Get API key from storage or settings
   */
  const getApiKey = useCallback(() => {
    // Check storage for user-provided key (Pro tier)
    const storedKey = storage.get(API_KEY_KEY, null);
    if (storedKey) return storedKey;

    // Check for environment variable (Enterprise tier)
    if (typeof window !== 'undefined' && window.__CLAUDE_API_KEY__) {
      return window.__CLAUDE_API_KEY__;
    }

    return null;
  }, [storage]);

  /**
   * Get user's subscription tier
   */
  const getUserTier = useCallback(() => {
    const tier = storage.get(TIER_KEY, 'free');
    return tier || 'free';
  }, [storage]);

  /**
   * Get monthly usage count
   */
  const getMonthlyUsage = useCallback(() => {
    try {
      const usage = storage.get(USAGE_KEY, {});
      const monthKey = getCurrentMonthKey();
      return usage[monthKey] || 0;
    } catch {
      return 0;
    }
  }, [storage]);

  /**
   * Increment monthly usage
   */
  const incrementUsage = useCallback(() => {
    try {
      const usage = storage.get(USAGE_KEY, {});
      const monthKey = getCurrentMonthKey();
      usage[monthKey] = (usage[monthKey] || 0) + 1;
      storage.set(USAGE_KEY, usage);
    } catch (err) {
      console.error('Failed to track usage:', err);
    }
  }, [storage]);

  /**
   * Check if user can perform extraction
   */
  const canExtract = useCallback(() => {
    const tier = getUserTier();
    const tierConfig = TIER_LIMITS[tier];

    if (!tierConfig?.canImport) {
      return { allowed: false, reason: 'Your plan does not include AI import. Upgrade to Pro.' };
    }

    const usage = getMonthlyUsage();
    if (usage >= tierConfig.monthlyLimit && tierConfig.monthlyLimit !== Infinity) {
      return { allowed: false, reason: `Monthly limit of ${tierConfig.monthlyLimit} imports reached. Upgrade for more.` };
    }

    const apiKey = getApiKey();
    if (!apiKey) {
      return { allowed: false, reason: 'API key not configured. Add your Claude API key in Settings.' };
    }

    return { allowed: true };
  }, [getUserTier, getMonthlyUsage, getApiKey]);

  /**
   * Extract items from a document
   */
  const extractItems = useCallback(async (file, checklistType) => {
    setIsProcessing(true);
    setError(null);

    try {
      // Check permissions
      const { allowed, reason } = canExtract();
      if (!allowed) {
        throw new Error(reason);
      }

      // Get config
      const tier = getUserTier();
      const tierConfig = TIER_LIMITS[tier];
      const apiKey = getApiKey();

      // Convert file to base64
      const base64Content = await fileToBase64(file);
      const mediaType = getMediaType(file);

      // Call extraction service
      const result = await extractChecklistItems({
        base64Content,
        mediaType,
        checklistType,
        apiKey,
        model: tierConfig.model
      });

      // Track usage
      incrementUsage();

      // Store result
      setLastResult(result);

      return {
        success: true,
        data: result.data,
        model: result.model,
        usage: result.usage
      };
    } catch (err) {
      const errorMessage = err.message || 'Extraction failed';
      setError(errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    } finally {
      setIsProcessing(false);
    }
  }, [canExtract, getUserTier, getApiKey, incrementUsage]);

  /**
   * Get extraction statistics
   */
  const getStats = useCallback(() => {
    const tier = getUserTier();
    const tierConfig = TIER_LIMITS[tier];
    const usage = getMonthlyUsage();

    return {
      tier,
      monthlyLimit: tierConfig?.monthlyLimit || 0,
      monthlyUsage: usage,
      remaining: tierConfig?.monthlyLimit === Infinity
        ? Infinity
        : Math.max(0, (tierConfig?.monthlyLimit || 0) - usage),
      canExtract: canExtract().allowed
    };
  }, [getUserTier, getMonthlyUsage, canExtract]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return useMemo(() => ({
    // State
    isProcessing,
    error,
    lastResult,

    // Operations
    extractItems,
    canExtract,
    clearError,

    // Info
    getStats
  }), [isProcessing, error, lastResult, extractItems, canExtract, clearError, getStats]);
};

export default useAIExtraction;
